import { motion } from "framer-motion";
import React from "react";

// Floating animation for cards and decorative elements
export const floatingAnimation = {
  y: [0, -10, 0],
  transition: {
    duration: 3,
    repeat: Infinity,
    ease: "easeInOut",
  },
};

// Glow pulse animation
export const glowPulseAnimation = {
  boxShadow: [
    "0 0 20px rgba(52, 168, 81, 0.3)",
    "0 0 40px rgba(52, 168, 81, 0.5)",
    "0 0 20px rgba(52, 168, 81, 0.3)",
  ],
  transition: {
    duration: 2,
    repeat: Infinity,
    ease: "easeInOut",
  },
};

// Purple glow pulse for AI cards
export const purpleGlowPulse = {
  boxShadow: [
    "0 0 20px rgba(108, 89, 166, 0.3)",
    "0 0 40px rgba(108, 89, 166, 0.5)",
    "0 0 20px rgba(108, 89, 166, 0.3)",
  ],
  transition: {
    duration: 2,
    repeat: Infinity,
    ease: "easeInOut",
  },
};

// Card entrance animation with stagger
export const cardEntranceVariants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95,
  },
  visible: (index = 0) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      delay: index * 0.15,
      ease: [0.25, 0.46, 0.45, 0.94], // Custom easing
    },
  }),
};

// Floating card animation (continuous)
export const floatingCardVariants = {
  animate: {
    y: [0, -15, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

// Decorative shape floating animation
export const decorativeFloatVariants = {
  animate: {
    y: [0, -20, 0],
    rotate: [0, 5, -5, 0],
    opacity: [0.6, 1, 0.6],
    transition: {
      duration: 5,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

// Number counter animation hook
export const useCountUp = (end, duration = 1.5) => {
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    let startTime = null;
    let animationFrameId = null;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min(
        (currentTime - startTime) / (duration * 1000),
        1
      );
      const currentCount = Math.floor(progress * end);
      setCount(currentCount);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [end, duration]);

  return count;
};

// Hero section animation variants
export const heroVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

// Stats animation variants
export const statsVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: (index = 0) => ({
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      delay: 0.3 + index * 0.1,
      ease: "easeOut",
    },
  }),
};

// Button hover variants
export const buttonHoverVariants = {
  rest: { scale: 1 },
  hover: {
    scale: 1.05,
    transition: {
      duration: 0.2,
      ease: "easeOut",
    },
  },
  tap: {
    scale: 0.95,
  },
};

// Container for stagger children
export const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};


